/**
 * The form runner (US B2/B3/B4) — the primary deliverable of this app. Wires together
 * engine/renderer.js (render + relevant/calculate/repeat), engine/audit-client.js
 * (realtime findings via @farmer/audit-rules — ADR-8), ui/findings-panel.js, offline/db.js +
 * offline/queue.js (autosave resilience, farmer-offline-sync skill), and ui/readonly.js
 * (locking once status != 'draft').
 *
 * ADR-8 reminder: the client audit panel is UX only. The server re-runs the same checks
 * on submit and its `audit_findings` are the record of truth — see the submit handler
 * below for how a 422 replaces this panel's contents with the server's result.
 *
 * What gates submission is only the questionnaire's own `required` / `constraint`
 * (`REQ:` / `CONS:` findings). The A/B agronomic rules run and are shown, but they are
 * cross-check material for the supervisor at approval time and never block the
 * enumerator — see the panel's header comment and submissions.submit() on the server.
 */
import * as api from '../api-client.js';
import { createState } from '../engine/state.js';
import { createFormRenderer } from '../engine/renderer.js';
import { runRealtimeAudit, runRealtimeCompleteness, normalizeStoredFinding } from '../engine/audit-client.js';
import { renderFindingsPanel, jumpToKey } from '../ui/findings-panel.js';
import { applyReadonly, renderStatusBanner } from '../ui/readonly.js';
import { createFormRail } from '../ui/form-rail.js';
import { renderFormReview } from '../ui/form-review.js';
import { sectionStates } from '../engine/review-model.js';
import { renderQuickCheck } from '../ui/quick-check.js';
import { quickCheckRows } from '../engine/quick-check.js';
import { createExportMenu } from '../ui/export-menu.js';
import { saveDraft, loadDraft, deleteDraft } from '../offline/db.js';
import { enqueue, onPrunedKeys, queueLength } from '../offline/queue.js';
import { toast, debounce, esc, formatDateTime } from '../ui/util.js';
import { requiredProgress, surveyOutline, formLabel, FORM_LABELS } from '@farmer/audit-rules';
import { formatMetricValue, metricLabel, sortMetrics } from '../ui/metrics.js';
import { middlemanTypeLabel } from './middlemen.js';
import { openProposalSheet } from '../ui/proposal-sheet.js';
import { shortId } from '../ui/proposal-fields.js';


/**
 * Thẻ Hộ/Vườn đầu phiếu. Bản HTML cũ hỏi những thông tin này như câu hỏi (Phần I
 * và Phần II); ở đây chúng là master data (R1) nên chỉ hiển thị, kèm lối tắt sang
 * hồ sơ Hộ để sửa đúng chỗ thay vì sửa trong phiếu.
 *
 * Phiếu nháp tính theo giá trị MỚI NHẤT, kể cả đề xuất chưa duyệt (Q1) — máy chủ trả
 * `pendingMasterData` cho những trường đang như vậy, và thẻ phải nói rõ con số đang dùng
 * là con số chờ duyệt, không phải con số trong hồ sơ. Nút "Báo sai thông tin" (E3) mở
 * sheet đề xuất ngay trong phiếu; `renderSubmissionForm` nối hành vi cho nó.
 */
function renderMasterCard(host, submission) {
  const h = submission.household || {};
  const mm = submission.middleman;
  const p = submission.plot;
  const draft = submission.status === 'draft';
  const pend = draft ? submission.pendingMasterData || {} : {};
  const areaHa = pend.areaHa?.value ?? submission.snapshotAreaHa ?? p?.areaHa;
  const soilType = pend.soilType?.value ?? p?.soilType;
  const pendNote = (x, base) =>
    x ? `Mới nhất · đề xuất ${shortId(x.changeRequestId)} chờ duyệt (hồ sơ: ${base ?? '—'})` : '';
  // Phiếu có đúng MỘT chủ thể (Doc/09 lát 2): Hộ (+ Vườn) hoặc Đại lý/MM.
  const fields = (
    mm
      ? [
          ['storefront', 'Đại lý / MM', mm.name],
          ['tag', 'Mã MM', mm.code],
          ['category', 'Loại', middlemanTypeLabel(mm.type)],
        ]
      : [
          ['person', 'Chủ hộ', h.fullName],
          ['badge', 'Mã nông dân', h.farmerCode],
          ['call', 'Điện thoại', h.phone],
          ['crop_square', 'Vườn', p?.plotCode],
          ['straighten', 'Diện tích', areaHa ? `${areaHa} ha` : '', pendNote(pend.areaHa, p?.areaHa && `${p.areaHa} ha`)],
          ['terrain', 'Thành phần đất', soilType, pendNote(pend.soilType, p?.soilType)],
        ]
  ).filter(([, , v]) => v !== undefined && v !== null && String(v) !== '');

  host.innerHTML = `
    <div class="master-card-items">
      ${fields
        .map(
          ([icon, label, value, note]) => `<div class="master-item">
            <span class="ms">${icon}</span>
            <span><span class="master-label">${esc(label)}</span><span class="master-value">${esc(value)}</span>${
              note ? `<span class="pending-chip">${esc(note)}</span>` : ''
            }</span>
          </div>`,
        )
        .join('')}
    </div>
    <div class="master-card-actions">
      ${draft && h.id ? `<button type="button" class="btn btn-tonal sm" data-report="household"><span class="ms">flag</span>Báo sai thông tin hộ</button>` : ''}
      ${draft && p?.id ? `<button type="button" class="btn btn-tonal sm" data-report="plot"><span class="ms">flag</span>Báo sai thông tin vườn</button>` : ''}
      ${draft && mm?.id ? `<button type="button" class="btn btn-tonal sm" data-report="middleman"><span class="ms">flag</span>Báo sai thông tin MM</button>` : ''}
      ${h.id ? `<a class="btn btn-text sm" href="#/households/${esc(h.id)}"><span class="ms">open_in_new</span>Hồ sơ hộ</a>` : ''}
      ${mm?.id ? `<a class="btn btn-text sm" href="#/middlemen/${esc(mm.id)}"><span class="ms">open_in_new</span>Hồ sơ MM</a>` : ''}
      <span class="hint-text">${
        draft
          ? `Thông tin ${mm ? 'MM' : 'hộ/vườn'} là dữ liệu chủ — thấy sai thì báo, người duyệt sửa hồ sơ.`
          : 'Giá trị đã chốt tại thời điểm nộp phiếu.'
      }</span>
    </div>`;
}

/**
 * Chỉ số máy chủ trích lúc nộp (`submission_values`): mật độ gốc/ha, năng suất/ha,
 * phân bón/ha. Chúng được tính và lưu ngay từ bản đầu nhưng chưa màn hình nào đọc.
 *
 * Chỉ hiện với phiếu đã khoá. Với phiếu nháp chúng chưa tồn tại — máy chủ tính
 * chúng trong chính giao dịch nộp phiếu — nên hiện một thẻ rỗng ở đó chỉ khiến
 * người nhập tưởng có gì đó hỏng.
 *
 * Đây là những con số người duyệt đối chiếu: "2.500 gốc/ha" nói nhanh hơn nhiều so
 * với việc tự cộng bảy nhóm tuổi rồi chia cho diện tích.
 */
function renderMetricsCard(host, submission) {
  const values = sortMetrics(submission.values || []);
  if (submission.status === 'draft' || !values.length) {
    host.innerHTML = '';
    return;
  }
  host.innerHTML = `
    <div class="card metrics-card">
      <h2 class="card-title"><span class="ms">insights</span>Chỉ số đã tính khi nộp</h2>
      <div class="metrics-grid">
        ${values
          .map(
            (v) => `<div class="metric-box">
              <span class="metric-box-label">${esc(metricLabel(v.metricKey))}</span>
              <span class="metric-box-value">${esc(formatMetricValue(v))}</span>
            </div>`,
          )
          .join('')}
      </div>
      <p class="hint-text">Tính từ câu trả lời và diện tích đã chốt tại thời điểm nộp phiếu.</p>
    </div>`;
}

/** Dưới mốc này bảng rộng khó dùng, giữ giao diện điện thoại (Doc/11a, "Rủi ro"). */
const PAPER_MIN_WIDTH = '(min-width: 1024px)';

/** Dòng chủ thể cố định trên cùng: luôn thấy đang nhập cho ai, diện tích nào. */
function subjectSummary(submission) {
  const mm = submission.middleman;
  if (mm) return { title: mm.name, detail: mm.code || '', pending: false };
  const p = submission.plot;
  const pend = submission.status === 'draft' ? submission.pendingMasterData?.areaHa : null;
  const areaHa = pend?.value ?? submission.snapshotAreaHa ?? p?.areaHa;
  return {
    title: submission.household?.fullName || '',
    detail: [p?.plotCode, areaHa ? `${areaHa} ha` : ''].filter(Boolean).join(' · '),
    pending: !!pend,
  };
}

/**
 * Lời nhắc trước khi nộp khi phiếu đang chốt giá trị của đề xuất chưa duyệt (Q1), `null` nếu không có.
 * Nộp xong con số đó thành snapshot, nên người nhập phải biết mình đang chốt số nào.
 */
function pendingSubmitWarning(submission) {
  const pend = submission.pendingMasterData || {};
  const parts = [];
  if (pend.areaHa) parts.push(`diện tích ${pend.areaHa.value} ha (hồ sơ ghi ${submission.plot?.areaHa ?? '—'} ha)`);
  if (pend.soilType) parts.push(`thành phần đất "${pend.soilType.value}" (hồ sơ ghi "${submission.plot?.soilType ?? '—'}")`);
  if (!parts.length) return null;
  return (
    `Phiếu sẽ chốt ${parts.join(' và ')} theo đề xuất đang chờ duyệt.\n` +
    'Nếu đề xuất bị từ chối, phiếu được gắn cờ để người duyệt xem lại.\n\nNộp phiếu?'
  );
}

export async function renderSubmissionForm(container, { id }) {
  container.innerHTML = `<div class="empty-state">Đang tải phiếu...</div>`;

  let submission = null;
  let offlineOnly = false;
  try {
    submission = await api.submissions.get(id);
  } catch (err) {
    if (isNetworkError(err)) {
      const draft = await loadDraft(id);
      if (draft) {
        submission = draft.submission;
        offlineOnly = true;
      } else {
        container.innerHTML = `<div class="empty-state"><span class="ms ms-xl">cloud_off</span>
          <p>Không có mạng và chưa có bản nháp lưu tại máy cho phiếu này. Cần có mạng để mở phiếu lần đầu.</p></div>`;
        return;
      }
    } else {
      container.innerHTML = `<div class="empty-state">${esc(err.message)}</div>`;
      return;
    }
  }

  const user = api.getCurrentUser();
  // Quản trị đơn vị duyệt/từ chối/mở lại phiếu trong đơn vị mình (Doc/06 muc 3.4).
  const isSupervisor =
    !!user && ['supervisor', 'instance_admin', 'admin'].includes(user.role);
  const readonly = submission.status !== 'draft';
  // Quyết định một lần lúc mở phiếu: đổi giữa chừng phải dựng lại cả form đang nhập dở.
  const paper = window.matchMedia(PAPER_MIN_WIDTH).matches;
  const subject = subjectSummary(submission);
  const syncIndicatorHtml = `<span class="sync-indicator" id="formSyncIndicator"><span class="ms">cloud_done</span><span>Đã lưu</span></span>`;

  container.innerHTML = `
    <div class="page-head form-head">
      <div class="form-head-main">
        <h1>${esc(formLabel(submission.formKey))}</h1>
        <p class="form-head-sub">${esc(FORM_LABELS[submission.formKey]?.description || '')}</p>
      </div>
      <div class="form-head-actions">
        ${paper ? '' : syncIndicatorHtml}
        <button class="icon-btn" id="collapseBtn" title="Thu gọn / mở tất cả phần" aria-label="Thu gọn hoặc mở tất cả phần"><span class="ms">unfold_less</span></button>
        <button class="icon-btn" id="exportBtn" title="Xuất dữ liệu" aria-haspopup="menu" aria-expanded="false" aria-label="Xuất dữ liệu"><span class="ms">download</span></button>
      </div>
    </div>

    ${
      paper
        ? `<div class="form-subject-bar" id="subjectBar">
             <div class="fsb-main">
               <b>${esc(subject.title)}</b>
               ${subject.detail ? `<span>${esc(subject.detail)}</span>` : ''}
               ${subject.pending ? '<span class="pending-chip">mới nhất · chờ duyệt</span>' : ''}
             </div>
             <div class="fsb-right">
               ${syncIndicatorHtml}
               <div class="mode-switch" role="group" aria-label="Chế độ xem phiếu">
                 <button type="button" data-mode="entry" aria-pressed="true">${readonly ? 'Bản nhập' : 'Nhập'}</button>
                 <button type="button" data-mode="review" aria-pressed="false">Xem lại toàn phiếu</button>
               </div>
             </div>
           </div>`
        : ''
    }

    <div class="master-card" id="masterCard"></div>
    <div id="metricsHost"></div>
    <div id="statusBannerHost"></div>
    <div id="approvalActions"></div>

    <div class="form-toolbar" id="formToolbar">
      <div class="searchbar">
        <span class="ms">search</span>
        <input type="search" id="formSearch" placeholder="Tìm câu hỏi, gợi ý, tên biến…" aria-label="Tìm câu hỏi trong phiếu">
      </div>
      <span class="hint-text" id="searchHint"></span>
      <div class="progress-wrap" title="Số câu bắt buộc đã điền">
        <div class="progress-bar"><span id="progressFill"></span></div>
        <span class="hint-text" id="progressText"></span>
      </div>
    </div>

    <div class="form-layout">
      <nav class="form-rail" id="railHost"></nav>
      <div class="survey-form-col">
        <div id="formHost" class="survey-form-host"></div>
        <div id="reviewHost" class="form-review" hidden></div>
      </div>
      <div class="form-side">
        <div class="card" id="quickCheckHost" hidden></div>
        <div class="card findings-panel" id="findingsHost"></div>
      </div>
    </div>

    <div class="card submit-bar" id="submitBar">
      <button class="btn" id="reviewBtn" hidden><span class="ms">description</span>Xem lại &amp; nộp</button>
      <button class="btn" id="submitBtn"><span class="ms">send</span>Nộp phiếu</button>
      <button class="btn btn-text" id="backToEntryBtn" hidden><span class="ms">edit</span>Quay lại nhập</button>
    </div>`;

  renderMasterCard(container.querySelector('#masterCard'), submission);
  renderMetricsCard(container.querySelector('#metricsHost'), submission);

  renderStatusBanner({
    host: container.querySelector('#statusBannerHost'),
    status: submission.status,
    canReopen: isSupervisor && submission.status !== 'draft',
    onReopen: async () => {
      const reason = window.prompt('Lý do mở lại phiếu (bắt buộc):');
      if (!reason || !reason.trim()) return;
      try {
        await api.submissions.reopen(id, reason.trim());
        toast('Đã mở lại phiếu');
        location.reload();
      } catch (err) {
        toast('Không mở lại được: ' + err.message);
      }
    },
  });

  if (isSupervisor && submission.status === 'submitted') {
    const actionsHost = container.querySelector('#approvalActions');
    actionsHost.innerHTML = `<div class="status-banner tone-info">
      <span class="ms">rate_review</span><span>Phiếu chờ duyệt.</span>
      <button class="btn btn-tonal sm" id="approveBtn">Duyệt</button>
      <button class="btn btn-text sm danger" id="rejectBtn">Từ chối</button>
    </div>`;
    actionsHost.querySelector('#approveBtn').addEventListener('click', async () => {
      try {
        await api.submissions.approve(id);
        toast('Đã duyệt phiếu');
        location.reload();
      } catch (err) {
        toast('Lỗi: ' + err.message);
      }
    });
    actionsHost.querySelector('#rejectBtn').addEventListener('click', async () => {
      const reason = window.prompt('Lý do từ chối (bắt buộc):');
      if (!reason || !reason.trim()) return;
      try {
        await api.submissions.reject(id, reason.trim());
        toast('Đã từ chối phiếu');
        location.reload();
      } catch (err) {
        toast('Lỗi: ' + err.message);
      }
    });
  }

  // ---------------------------------------------------------------- engine
  const state = createState(submission.answers || {});
  const formHost = container.querySelector('#formHost');
  const findingsHost = container.querySelector('#findingsHost');
  const progressText = container.querySelector('#progressText');
  const progressFill = container.querySelector('#progressFill');
  const syncIndicator = container.querySelector('#formSyncIndicator');
  const submitBtn = container.querySelector('#submitBtn');

  let findingsCtrl = null;

  // R1 — Hộ/Vườn không còn là câu hỏi; API trả lớp chiếu chỉ đọc để nhãn `${...}` và
  // bộ luật audit (cần `coffee_area`) nhìn thấy đúng như server nhìn thấy lúc nộp.
  const masterData = submission.masterData || {};

  // Luật đối chiếu admin đã tắt (`#/audit-rules`). Không lấy được thì coi như không
  // tắt luật nào: bảng cảnh báo hiện thừa vài dòng vẫn hơn là im lặng bỏ sót — và
  // server mới là nơi quyết định cuối cùng những dòng nào được ghi lại.
  // Cùng lượt gọi đó lấy luôn text tuỳ chỉnh admin đặt cho từng luật — không lấy
  // được thì bảng hiện text gốc, lệch chữ với server nhưng không lệch số.
  let disabledRules = [];
  let ruleText = {};
  try {
    const catalog = await api.auditRules.list(submission.formKey);
    disabledRules = catalog.filter((r) => !r.enabled).map((r) => r.code);
    ruleText = Object.fromEntries(
      catalog
        .filter((r) => r.titleText || r.fixText)
        .map((r) => [
          r.code,
          { ...(r.titleText ? { title: r.titleText } : {}), ...(r.fixText ? { fix: r.fixText } : {}) },
        ]),
    );
  } catch (err) {
    console.warn('[submission-form] không tải được danh mục luật đối chiếu', err);
  }

  const renderer = createFormRenderer({
    container: formHost,
    schema: submission.formSchemaSnapshot,
    state,
    formKey: submission.formKey,
    masterData,
    // Danh mục MM của đơn vị sở hữu phiếu — API trả kèm, cùng danh sách server kiểm lúc nộp.
    dynamicLists: submission.dynamicLists,
    paper,
    onUpdate: () => {
      updateProgress();
      scheduleDraftSave();
      scheduleAudit();
      scheduleAutosave();
    },
  });
  renderer.mount();
  applyReadonly(formHost, readonly);

  /**
   * Tiến độ đếm từ chính bộ câu hỏi (@farmer/audit-rules), không đếm bằng DOM: câu hỏi
   * nằm trong Table Matrix không có khối `.q` riêng nên cách đếm cũ bỏ sót toàn bộ
   * nhóm lặp và luôn báo "đủ" ngay cả khi còn ô bắt buộc trống, mâu thuẫn với chính
   * điều kiện chặn nộp phiếu.
   */
  function updateProgress() {
    const { total, done } = requiredProgress(
      submission.formSchemaSnapshot,
      { ...state.all(), ...masterData },
      submission.formKey,
    );
    progressText.textContent = `${done}/${total} câu bắt buộc`;
    progressFill.style.width = total ? `${Math.round((done / total) * 100)}%` : '0%';
  }
  updateProgress();

  // -------------------------------------------------------- nhập / xem lại toàn phiếu
  const reviewHost = container.querySelector('#reviewHost');
  const toolbar = container.querySelector('#formToolbar');
  const reviewBtn = container.querySelector('#reviewBtn');
  const backToEntryBtn = container.querySelector('#backToEntryBtn');
  let mode = 'entry';
  let lastFindings = [];

  const currentOutline = () =>
    surveyOutline(submission.formSchemaSnapshot, { ...state.all(), ...masterData }, submission.formKey, {
      dynamicLists: submission.dynamicLists,
    });

  function paintReview(outline) {
    renderFormReview(reviewHost, {
      outline,
      findings: lastFindings,
      readonly,
      onEditSection: (sectionId) => {
        setMode('entry');
        const section = renderer.getSections().find((s) => s.id === sectionId);
        if (!section) return;
        section.el.classList.remove('collapsed');
        section.el.querySelector('.form-section-head')?.setAttribute('aria-expanded', 'true');
        section.el.scrollIntoView({ block: 'start' });
      },
      onJumpToKey: (key) => {
        setMode('entry');
        if (key) jumpToKey(formHost, key);
      },
    });
  }

  function setMode(next) {
    mode = next;
    const reviewing = next === 'review';
    formHost.hidden = reviewing;
    reviewHost.hidden = !reviewing;
    toolbar.hidden = reviewing;
    container.querySelectorAll('[data-mode]').forEach((btn) => {
      btn.setAttribute('aria-pressed', String(btn.dataset.mode === next));
    });
    if (reviewing) {
      // Câu trả lời có thể vừa đổi mà bảng cảnh báo chưa tới lượt chạy lại: xem lại phải nói đúng lúc này.
      if (!readonly) {
        scheduleAudit.cancel();
        runAndRenderAudit();
      }
      paintReview(currentOutline());
      // Cuộn theo bản xem lại, không theo thanh chủ thể: thanh đó dính nên lúc nào cũng "đang thấy".
      reviewHost.scrollIntoView({ block: 'start' });
    }
    updateSubmitState();
  }

  /** Mục lục và bản xem lại đọc cùng tập finding với bảng cảnh báo. */
  function afterFindings(findings) {
    lastFindings = findings;
    if (!paper) return;
    // Phiếu đã khoá có thẻ "Chỉ số đã tính khi nộp" với số chính thức, không cần số tạm ở đây.
    if (!readonly) {
      renderQuickCheck(
        container.querySelector('#quickCheckHost'),
        quickCheckRows({
          formKey: submission.formKey,
          answers: { ...renderer.getVisibleAnswers(), ...masterData },
          findings,
          areaPending: !!submission.pendingMasterData?.areaHa,
        }),
      );
    }
    const outline = currentOutline();
    rail.setBadges(sectionStates(outline, findings));
    if (mode === 'review') paintReview(outline);
  }

  container.querySelectorAll('[data-mode]').forEach((btn) =>
    btn.addEventListener('click', () => setMode(btn.dataset.mode)),
  );
  reviewBtn.addEventListener('click', () => setMode('review'));
  backToEntryBtn.addEventListener('click', () => setMode('entry'));

  // Thanh chủ thể dính ngay dưới thanh trên cùng; mục lục và bảng cảnh báo dính dưới nó. Thanh trên cùng
  // xuống hai dòng khi menu dài, nên đo thật thay vì viết cứng.
  let stickyObserver = null;
  if (paper) {
    const header = document.querySelector('.app-header');
    const bar = container.querySelector('#subjectBar');
    const syncSticky = () => {
      const top = header?.getBoundingClientRect().height || 0;
      container.style.setProperty('--subject-bar-top', `${top}px`);
      container.style.setProperty('--rail-offset', `${top + bar.offsetHeight + 8}px`);
    };
    stickyObserver = new ResizeObserver(syncSticky);
    if (header) stickyObserver.observe(header);
    stickyObserver.observe(bar);
    syncSticky();
  }

  // -------------------------------------------------------- mục lục, tìm kiếm
  const rail = createFormRail(container.querySelector('#railHost'), renderer, {
    onSelect: (section) => {
      if (mode !== 'review') return false;
      document.getElementById(`rev-${section.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return true;
    },
  });

  const exportMenu = createExportMenu({
    button: container.querySelector('#exportBtn'),
    submission,
    // Chỉ câu đang hiện — cùng tập được lưu và ra file xuất của máy chủ. `state.all()` còn
    // chở câu vừa ẩn (chưa tới lượt tự lưu, hoặc đang ngoại tuyến) và ô tính của nhánh ẩn.
    getAnswers: () => renderer.getVisibleAnswers(),
    masterData,
    // XLSForm là tài sản của phiên bản form, không phải của phiếu — chỉ admin xuất
    // được (GET /export/xlsform/:formKey), nên người khác không thấy mục này.
    canExportXlsForm: user?.role === 'admin',
  });

  const collapseBtn = container.querySelector('#collapseBtn');
  let allCollapsed = false;
  collapseBtn.addEventListener('click', () => {
    allCollapsed = !allCollapsed;
    renderer.setAllCollapsed(allCollapsed);
    collapseBtn.innerHTML = `<span class="ms">${allCollapsed ? 'unfold_more' : 'unfold_less'}</span>`;
  });

  const searchInput = container.querySelector('#formSearch');
  const searchHint = container.querySelector('#searchHint');
  const applySearch = debounce(() => {
    const q = searchInput.value.trim();
    const matches = renderer.setSearch(q);
    searchHint.textContent = q ? `${matches} câu hỏi khớp "${q}"` : '';
  }, 180);
  searchInput.addEventListener('input', applySearch);
  searchInput.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      searchInput.value = '';
      applySearch.flush();
    }
  });

  // ------------------------------------------------------------- audit panel
  function runAndRenderAudit() {
    const answers = { ...renderer.getVisibleAnswers(), ...masterData };
    // Hai điều kiện nộp phiếu, cùng thứ tự server dùng: (1) đủ câu bắt buộc + đúng
    // `constraint` của bảng hỏi, (2) hợp lý theo bộ luật nông học A/B. Nhóm (1) lên
    // trước vì đó là việc phải sửa, không ghi đè được.
    //
    // Phần (1) nhận `state.all()` chứ không phải bản đã lọc nhánh ẩn: nó tự đánh giá
    // `relevant`, và để làm được thì phải nhìn thấy cả ô `calculate` của nhánh đang ẩn
    // — đúng tập dữ liệu server nhìn thấy khi nộp.
    const findings = [
      ...runRealtimeCompleteness(
        submission.formKey,
        submission.formSchemaSnapshot,
        { ...state.all(), ...masterData },
        { dynamicLists: submission.dynamicLists },
      ),
      // `auditConfig` = ngưỡng của chính phiên bản phiếu này (API trả kèm phiếu). Bỏ
      // trống thì audit-client rơi về DEFAULT_AUDIT_CONFIG, tức bảng cảnh báo chạy trên
      // ngưỡng biên dịch sẵn trong mã còn server chạy trên ngưỡng đã snapshot — admin
      // sửa ngưỡng xong vẫn thấy cảnh báo theo số cũ, rồi nó biến mất sau khi nộp.
      ...runRealtimeAudit(submission.formKey, answers, submission.formSchemaSnapshot.choices, {
        disabledRules,
        ruleText,
        auditConfig: submission.auditRulesSnapshot,
      }),
    ];
    findingsCtrl = renderFindingsPanel({
      container: findingsHost,
      findings,
      formContainer: formHost,
      editable: !readonly,
      beforeJump: () => setMode('entry'),
      reasons: findingsCtrl?.getOverrides(),
    });
    afterFindings(findings);
    updateSubmitState();
  }

  function updateSubmitState() {
    if (readonly) {
      container.querySelector('#submitBar').hidden = true;
      return;
    }
    // Trên máy tính nút Nộp chỉ ở bản xem lại: người nhập phải đọc lại cả phiếu trước (Doc/11a).
    const reviewing = mode === 'review';
    submitBtn.hidden = paper && !reviewing;
    reviewBtn.hidden = !paper || reviewing;
    backToEntryBtn.hidden = !paper || !reviewing;
    // Chỉ điều kiện của bảng hỏi mới khoá nút Nộp. Bộ luật A/B là số liệu đối chiếu
    // cho người duyệt — nó hiện trong bảng bên cạnh nhưng không chặn điều tra viên.
    const blocking = findingsCtrl ? findingsCtrl.getBlocking() : [];
    submitBtn.disabled = blocking.length > 0;
    submitBtn.title = blocking.length
      ? `Còn ${blocking.length} câu bắt buộc chưa điền hoặc giá trị không hợp lệ theo bảng hỏi`
      : '';
  }
  const scheduleAudit = debounce(runAndRenderAudit, 400);

  if (readonly) {
    // Show the server's last confirmed findings (DB rows) instead of a live re-run — a
    // locked submission's answers can't change, so there's nothing "realtime" left to do.
    const stored = (submission.findings || submission.auditFindings || []).map(normalizeStoredFinding);
    findingsCtrl = renderFindingsPanel({
      container: findingsHost,
      findings: stored,
      formContainer: formHost,
      editable: false,
      beforeJump: () => setMode('entry'),
    });
    afterFindings(stored);
    updateSubmitState();
  } else {
    runAndRenderAudit();
  }

  // ------------------------------------------------------------ autosave
  let lastSyncedAt = submission.updatedAt || null;

  function setSyncState(kind) {
    const map = {
      synced: ['sync-indicator', 'cloud_done', lastSyncedAt ? `Đã lưu lúc ${formatDateTime(lastSyncedAt)}` : 'Đã lưu'],
      saving: ['sync-indicator warn', 'sync', 'Đang lưu...'],
      queued: ['sync-indicator warn', 'schedule', 'Chưa đồng bộ — sẽ gửi khi có mạng'],
      error: ['sync-indicator error', 'error', 'Không lưu được — xem chi tiết'],
    };
    const [cls, icon, text] = map[kind] || map.synced;
    syncIndicator.className = cls;
    syncIndicator.innerHTML = `<span class="ms">${icon}</span><span>${esc(text)}</span>`;
  }

  const scheduleDraftSave = debounce(() => {
    saveDraft(id, { submission: { ...submission, answers: state.all() } });
  }, 500);

  // Named + extracted (rather than only living inside the debounce) so submit can `await
  // doAutosave()` directly instead of firing the debounced wrapper and racing it — the
  // server's submit endpoint audits/locks whatever is already persisted in `answers`, not
  // anything passed in the submit request body, so any not-yet-sent edit must land first.
  /**
   * Có người khác vừa sửa phiếu này. Thay đổi vừa gõ ĐÃ được lưu (máy chủ merge
   * chứ không từ chối), nên đây không phải lỗi — chỉ là điều người dùng bắt buộc
   * phải biết.
   *
   * Không tự trộn giá trị của người kia vào form đang mở: thay chữ dưới tay người
   * đang gõ giữa cuộc phỏng vấn còn tệ hơn là để họ tự quyết lúc nào tải lại. Chỉ
   * hiện băng thông báo, kèm chính những ô đã lệch để họ biết cần nhìn vào đâu.
   */
  let banerDongThoi = null;
  function baoSuaDongThoi(answersMayChu) {
    const hienTai = state.all();
    const oLech = Object.keys(answersMayChu || {}).filter(
      (k) => k in hienTai && String(answersMayChu[k]) !== String(hienTai[k]),
    );

    if (!banerDongThoi) {
      banerDongThoi = document.createElement('div');
      container.querySelector('#statusBannerHost').appendChild(banerDongThoi);
    }
    banerDongThoi.className = 'status-banner tone-warn';
    banerDongThoi.innerHTML = `
      <span class="ms">group</span>
      <span>Phiếu này vừa được sửa ở nơi khác. Thay đổi của bạn đã lưu.
        ${oLech.length ? `<b>${oLech.length} ô</b> đang khác với bản trên máy chủ.` : ''}
      </span>
      <button class="btn btn-tonal sm" type="button" data-act="tai-lai">Tải lại phiếu</button>`;
    banerDongThoi
      .querySelector('[data-act="tai-lai"]')
      .addEventListener('click', () => location.reload());
  }

  /**
   * Tra ve `true` khi cac thay doi da nam an toan (da ghi len may chu, hoac da
   * vao hang doi de dong bo lai), `false` khi that bai han.
   *
   * Gia tri tra ve khong phai de trang tri: lenh nop goi ham nay truoc va PHAI
   * dung lai neu no tra `false`. Truoc day ham nay tu bat het loi va khong bao
   * ra ngoai, nen mot lan autosave that bai ngay truoc khi nop se lam phieu duoc
   * nop MA THIEU cac cau tra loi moi nhat — hong am tham, khong ai biet.
   */
  async function doAutosave() {
    if (readonly) return true;
    const dirty = state.getDirty();
    const removeKeys = state.getRemoved();
    if (!Object.keys(dirty).length && !removeKeys.length) return true;
    const visible = renderer.getVisibleAnswers();
    const answersToSend = {};
    Object.keys(dirty).forEach((k) => {
      if (k in visible) answersToSend[k] = dirty[k];
    });
    setSyncState('saving');
    try {
      // Gửi kèm mốc lần lưu gần nhất: máy chủ vẫn merge nhưng báo lại nếu có
      // người khác đã sửa phiếu này trong lúc đó.
      const res = await api.submissions.patchAnswers(id, answersToSend, removeKeys, lastSyncedAt);
      lastSyncedAt = res.updatedAt || new Date().toISOString();
      state.markSynced(Object.keys(answersToSend));
      // Câu đang ẩn máy chủ đã xoá khỏi phiếu — bỏ luôn ở đây để câu hiện lại thì trống.
      if (res.prunedKeys?.length) renderer.forgetKeys(res.prunedKeys);
      setSyncState('synced');
      if (res.staleBase) baoSuaDongThoi(res.answers);
      return true;
    } catch (err) {
      if (isNetworkError(err)) {
        await enqueue({
          submissionId: id,
          kind: 'patchAnswers',
          payload: { answers: answersToSend, removeKeys, baseUpdatedAt: lastSyncedAt },
        });
        state.markSynced(Object.keys(answersToSend));
        setSyncState('queued');
        return true;
      }
      if (err.status === 409) {
        setSyncState('error');
        toast('Phiếu đã bị khoá (có thể đã được duyệt/nộp ở nơi khác) — không lưu được thay đổi này.');
        return false;
      }
      setSyncState('error');
      toast('Không lưu được: ' + err.message);
      return false;
    }
  }
  const scheduleAutosave = debounce(doAutosave, 2000);

  // E3 — báo sai Hộ/Vườn/MM ngay trong phiếu, không rời phiếu. Sheet đóng thì focus về nút.
  const reportRecord = { household: submission.household, plot: submission.plot, middleman: submission.middleman };
  const reportTitle = {
    household: submission.household?.fullName,
    plot: submission.plot?.plotCode || 'Vườn',
    middleman: submission.middleman?.name,
  };
  container.querySelectorAll('[data-report]').forEach((btn) =>
    btn.addEventListener('click', () => {
      const entity = btn.dataset.report;
      void openProposalSheet({
        entity,
        record: reportRecord[entity],
        title: reportTitle[entity] || '',
        link: `#/submissions/${id}`,
        onSent: async ({ queued, items }) => {
          // Q1: phiếu nháp tính ngay theo diện tích / thành phần đất vừa đề xuất. Giá trị đó
          // do máy chủ chiếu vào `masterData`, nên phải tải lại phiếu — nhưng chỉ sau khi
          // những gì vừa gõ đã lưu xong, và chỉ khi đề xuất đã tới máy chủ (đề xuất còn
          // trong hàng đợi thì máy chủ chưa biết gì để chiếu).
          if (queued || !items.some((it) => it.field === 'areaHa' || it.field === 'soilType')) return;
          scheduleAutosave.cancel();
          scheduleDraftSave.flush();
          // Thay đổi còn nằm trong hàng đợi thì tải lại sẽ hiện bản máy chủ thiếu chúng.
          if (!(await doAutosave()) || (await queueLength(id)) > 0) {
            toast('Đề xuất đã gửi. Mở lại phiếu sau khi lưu xong để thấy cảnh báo tính theo giá trị mới.');
            return;
          }
          window.dispatchEvent(new HashChangeEvent('hashchange'));
        },
      });
    }),
  );

  const onBeforeUnload = () => scheduleDraftSave.flush();
  window.addEventListener('beforeunload', onBeforeUnload);

  // Lưu từ hàng đợi ngoại tuyến cũng có thể bị máy chủ cắt câu ẩn — bỏ chúng ở đây như
  // `doAutosave` làm khi có mạng, để câu hiện lại thì trống thay vì mang số máy chủ đã xoá.
  const offPruned = onPrunedKeys(({ submissionId, keys }) => {
    if (submissionId === id && !readonly) renderer.forgetKeys(keys);
  });

  // ----------------------------------------------------------------- submit
  submitBtn.addEventListener('click', async () => {
    const warning = pendingSubmitWarning(submission);
    if (warning && !window.confirm(warning)) return;
    scheduleAutosave.cancel();
    const overrides = findingsCtrl ? findingsCtrl.getOverrides() : [];
    submitBtn.disabled = true;
    submitBtn.textContent = 'Đang lưu...';
    const daLuuAnToan = await doAutosave(); // must land before submit — see doAutosave's comment above
    if (!daLuuAnToan) {
      // Nộp lúc này là nộp phiếu thiếu những gì vừa nhập. Dừng lại và để điều tra
      // viên thử lại — toast giải thích lý do đã hiện từ trong doAutosave().
      toast('Chưa lưu được thay đổi mới nhất nên chưa nộp phiếu. Thử nộp lại sau vài giây.');
      submitBtn.textContent = 'Nộp phiếu';
      submitBtn.disabled = false;
      updateSubmitState();
      return;
    }
    submitBtn.textContent = 'Đang nộp...';
    try {
      await api.submissions.submit(id, overrides);
      toast('Đã nộp phiếu thành công');
      await deleteDraft(id);
      location.reload();
    } catch (err) {
      if (isNetworkError(err)) {
        await enqueue({ submissionId: id, kind: 'submit', payload: { overrides } });
        toast('Không có mạng — phiếu sẽ được nộp tự động khi có mạng trở lại.');
        setSyncState('queued');
      } else if (err.code === 'AUDIT_BLOCKED') {
        // Server chỉ trả về nhóm chặn (điều kiện bảng hỏi) — thay nội dung bảng bằng
        // đúng nhóm đó để điều tra viên không phải dò trong danh sách đối chiếu.
        const serverFindings = err.auditFindings || [];
        findingsCtrl = renderFindingsPanel({
          container: findingsHost,
          findings: serverFindings,
          formContainer: formHost,
          editable: true,
          beforeJump: () => setMode('entry'),
          reasons: findingsCtrl?.getOverrides(),
        });
        afterFindings(serverFindings);
        toast(`Phiếu chưa nộp được — còn ${serverFindings.length} câu chưa đạt điều kiện bảng hỏi (theo kiểm tra từ máy chủ).`);
      } else {
        toast('Không nộp được: ' + err.message);
      }
      updateSubmitState();
    } finally {
      submitBtn.textContent = 'Nộp phiếu';
      submitBtn.disabled = false;
      updateSubmitState();
    }
  });

  if (offlineOnly) {
    setSyncState('queued');
    toast('Đang xem bản nháp lưu tại máy (không có mạng). Thay đổi sẽ đồng bộ khi có mạng trở lại.');
  }

  // Returned to main.js's router, which calls this on navigating away — otherwise every
  // visit to a submission's form page would leak one more 'beforeunload' listener bound
  // to that visit's (by-then-stale) scheduleDraftSave closure.
  return () => {
    window.removeEventListener('beforeunload', onBeforeUnload);
    offPruned();
    stickyObserver?.disconnect();
    // `container` là khung chung của mọi màn: đừng để lại số đo của màn này cho màn sau.
    container.style.removeProperty('--subject-bar-top');
    container.style.removeProperty('--rail-offset');
    scheduleAudit.cancel();
    scheduleDraftSave.cancel();
    scheduleAutosave.cancel();
    applySearch.cancel();
    // Trình đơn xuất gắn vào document.body chứ không nằm trong `container`, nên
    // router dọn view không tự gỡ được — phải tự huỷ ở đây.
    rail.destroy();
    exportMenu.destroy();
  };
}

function isNetworkError(e) {
  if (e instanceof TypeError) return true;
  if (e?.name === 'AbortError') return true;
  // 429 khong phai loi cua nguoi dung va se tu khoi — xu ly nhu mat mang: xep
  // vao hang doi roi thu lai. Truoc day 429 roi vao nhanh "loi khac" nen hien
  // toast do lien tuc giua luc dieu tra vien dang go, va lenh nop thi bao that
  // bai ma khong thu lai.
  if (e?.status === 429) return true;
  return e?.status === undefined || e.status >= 500;
}
